import { API, Endpoints } from '@brg/api'

function pickPayload(o: Record<string, unknown>): Record<string, unknown> | null {
  const q = o.questions
  if (q == null) {
    return { ...o, questions: [] }
  }
  if (!Array.isArray(q)) {
    return null
  }
  return o
}

function pickRaw(data: Record<string, unknown>, keys: string[]): unknown {
  for (const key of keys) {
    if (key in data && data[key] != null) {
      return data[key]
    }
  }
  return undefined
}

function pickString(data: Record<string, unknown>, keys: string[]): string | null {
  const v = pickRaw(data, keys)
  if (typeof v === 'string' && v.trim()) return v
  return null
}

function pickDateString(data: Record<string, unknown>, keys: string[]): string | null {
  const v = pickRaw(data, keys)
  if (v instanceof Date && !Number.isNaN(v.getTime())) return v.toISOString()
  if (typeof v === 'number' && Number.isFinite(v)) {
    const d = new Date(v)
    return Number.isNaN(d.getTime()) ? null : d.toISOString()
  }
  if (typeof v === 'string' && v.trim()) {
    const d = new Date(v.trim())
    if (!Number.isNaN(d.getTime())) return d.toISOString()
    return v.trim()
  }
  return null
}

function pickNumber(data: Record<string, unknown>, keys: string[]): number | null {
  const v = pickRaw(data, keys)
  if (typeof v === 'number' && Number.isFinite(v)) return v
  if (typeof v === 'string' && v.trim()) {
    const n = Number(v)
    return Number.isFinite(n) ? n : null
  }
  return null
}

function pickStringArray(data: Record<string, unknown>, keys: string[]): string[] {
  const v = pickRaw(data, keys)
  if (!Array.isArray(v)) return []
  return v.map((item) => String(item ?? '').trim()).filter(Boolean)
}

export type LmsCustomVoteGroup = {
  groupId: string
  teacherIds: string[]
}

function pickCustomVoteGroups(
  data: Record<string, unknown>,
  keys: string[]
): LmsCustomVoteGroup[] {
  const v = pickRaw(data, keys)
  if (!Array.isArray(v)) return []
  const groups: LmsCustomVoteGroup[] = []
  v.forEach((item) => {
    if (item === null || typeof item !== 'object') return
    const row = item as Record<string, unknown>
    const groupId = pickString(row, ['groupId', 'GroupId']) ?? ''
    if (!groupId.trim()) return
    groups.push({
      groupId: groupId.trim(),
      teacherIds: pickStringArray(row, ['teacherIds', 'TeacherIds'])
    })
  })
  return groups
}

export type TestItemSopFields = {
  startDate: string | null
  endDate: string | null
  lmsVoteFormatType: number | null
  lmsVoteType: number | null
  lmsModuleId: string | null
  lmsModuleIds: string[]
  lmsVoteTeacherIds: string[]
  lmsVoteGroupIds: string[]
  lmsCustomVoteGroups: LmsCustomVoteGroup[]
}

export function pickTestItemSopFields(data: Record<string, unknown>): TestItemSopFields {
  return {
    startDate: pickDateString(data, ['startDate', 'StartDate', 'openAt', 'OpenAt']),
    endDate: pickDateString(data, ['endDate', 'EndDate', 'closeAt', 'CloseAt']),
    lmsVoteFormatType: pickNumber(data, ['lmsVoteFormatType', 'LMSVoteFormatType', 'LmsVoteFormatType']),
    lmsVoteType: pickNumber(data, ['lmsVoteType', 'LMSVoteType', 'LmsVoteType']),
    lmsModuleId: pickString(data, ['lmsModuleId', 'LMSModuleId', 'LmsModuleId']),
    lmsModuleIds: pickStringArray(data, ['lmsModuleIds', 'LMSModuleIds', 'LmsModuleIds']),
    lmsVoteTeacherIds: pickStringArray(data, [
      'lmsVoteTeacherIds',
      'LMSVoteTeacherIds',
      'LmsVoteTeacherIds',
    ]),
    lmsVoteGroupIds: pickStringArray(data, [
      'lmsVoteGroupIds',
      'LMSVoteGroupIds',
      'LmsVoteGroupIds',
    ]),
    lmsCustomVoteGroups: pickCustomVoteGroups(data, [
      'lmsCustomVoteGroups',
      'LMSCustomVoteGroups',
      'LmsCustomVoteGroups',
    ]),
  }
}

function withSopFields(payload: Record<string, unknown>): Record<string, unknown> {
  return { ...payload, ...pickTestItemSopFields(payload) }
}

export function unwrapTestItemPayload(raw: unknown): Record<string, unknown> | null {
  if (raw === null || typeof raw !== 'object') {
    return null
  }
  const root = raw as Record<string, unknown>
  const inner = root.data ?? root.Data
  if (inner !== null && typeof inner === 'object' && !Array.isArray(inner)) {
    const fromData = pickPayload(inner as Record<string, unknown>)
    if (fromData) {
      return withSopFields(fromData)
    }
    const d = inner as Record<string, unknown>
    return withSopFields({
      ...d,
      questions: Array.isArray(d.questions)
        ? d.questions
        : Array.isArray(d.Questions)
          ? d.Questions
          : []
    })
  }
  const fromRoot = pickPayload(root)
  return fromRoot ? withSopFields(fromRoot) : null
}

export const TEST_MANAGER_ITEM_STATUS_DRAFT = 0
export const TEST_MANAGER_ITEM_STATUS_PUBLISHED = 1
export const TEST_MANAGER_ITEM_STATUS_ARCHIVED = 2

function coalesceStatusNumber(v: unknown): number | null {
  if (typeof v === 'number' && Number.isFinite(v)) {
    return v
  }
  if (typeof v === 'string' && v.trim()) {
    const n = parseInt(v.trim(), 10)
    return Number.isFinite(n) ? n : null
  }
  return null
}

function isKnownManagerTestItemStatus(code: number): boolean {
  return (
    code === TEST_MANAGER_ITEM_STATUS_DRAFT ||
    code === TEST_MANAGER_ITEM_STATUS_PUBLISHED ||
    code === TEST_MANAGER_ITEM_STATUS_ARCHIVED
  )
}

export function pickManagerTestItemStatusCode(
  data: Record<string, unknown>
): number | null {
  const keys = ['status', 'Status', 'testItemStatus', 'TestItemStatus']
  for (const key of keys) {
    if (key in data) {
      const code = coalesceStatusNumber(data[key])
      if (code !== null && isKnownManagerTestItemStatus(code)) {
        return code
      }
    }
  }
  return null
}

export type FetchTestItemOptions = {
  accessToken?: string | null
  lmsModuleId?: string | null
}

export async function fetchTestItem(
  testItemId: string,
  options?: FetchTestItemOptions
): Promise<unknown> {
  const id = String(testItemId ?? '').trim()
  if (!id) {
    throw new Error('testItemId required')
  }

  let req = API.request()
    .endpoint(Endpoints.gateway)
    .url('testsservice/api/TestItem/Get')
    .addQuery('id', id)
    .get()

  const lmsModuleId = String(options?.lmsModuleId ?? '').trim()
  if (lmsModuleId) {
    req = req.addQuery('lmsModuleId', lmsModuleId)
  }

  const token = options?.accessToken?.trim()
  if (token) {
    req = req.headers({ Authorization: `Bearer ${token}` })
  }

  const response = await req.send()
  if (response.status === 204) {
    return null
  }
  const text = await response.text()

  if (!response.ok) {
    let msg = `HTTP ${response.status}`
    if (text.trim()) {
      try {
        const errBody = JSON.parse(text) as Record<string, unknown>
        const title = errBody.title
        const message = errBody.message ?? errBody.error
        if (typeof title === 'string') {
          msg = title
        } else if (typeof message === 'string') {
          msg = message
        }
      } catch {
      }
    }
    throw new Error(msg)
  }

  if (!text.trim()) {
    return {}
  }

  try {
    return JSON.parse(text) as unknown
  } catch {
    throw new Error('Invalid JSON response')
  }
}
import { API, Endpoints } from '@brg/api'

function pickPayload(o: Record<string, unknown>): Record<string, unknown> | null {
  const q = o.questions
  if (q == null) {
    return { ...o, questions: [] }
  }
  if (!Array.isArray(q)) {
    return null
  }
  return o
}

function pickRaw(data: Record<string, unknown>, keys: string[]): unknown {
  for (const key of keys) {
    if (key in data && data[key] != null) {
      return data[key]
    }
  }
  return undefined
}

function pickString(data: Record<string, unknown>, keys: string[]): string | null {
  const v = pickRaw(data, keys)
  if (typeof v === 'string' && v.trim()) return v
  return null
}

function pickDateString(data: Record<string, unknown>, keys: string[]): string | null {
  const v = pickRaw(data, keys)
  if (v instanceof Date && !Number.isNaN(v.getTime())) return v.toISOString()
  if (typeof v === 'number' && Number.isFinite(v)) {
    const d = new Date(v)
    return Number.isNaN(d.getTime()) ? null : d.toISOString()
  }
  if (typeof v === 'string' && v.trim()) {
    const d = new Date(v.trim())
    if (!Number.isNaN(d.getTime())) return d.toISOString()
    return v.trim()
  }
  return null
}

function pickNumber(data: Record<string, unknown>, keys: string[]): number | null {
  const v = pickRaw(data, keys)
  if (typeof v === 'number' && Number.isFinite(v)) return v
  if (typeof v === 'string' && v.trim()) {
    const n = Number(v)
    return Number.isFinite(n) ? n : null
  }
  return null
}

function pickStringArray(data: Record<string, unknown>, keys: string[]): string[] {
  const v = pickRaw(data, keys)
  if (!Array.isArray(v)) return []
  return v.map((item) => String(item ?? '').trim()).filter(Boolean)
}

export type LmsCustomVoteGroup = {
  groupId: string
  teacherIds: string[]
}

function pickCustomVoteGroups(
  data: Record<string, unknown>,
  keys: string[]
): LmsCustomVoteGroup[] {
  const v = pickRaw(data, keys)
  if (!Array.isArray(v)) return []
  const groups: LmsCustomVoteGroup[] = []
  v.forEach((item) => {
    if (item === null || typeof item !== 'object') return
    const row = item as Record<string, unknown>
    const groupId = pickString(row, ['groupId', 'GroupId']) ?? ''
    if (!groupId.trim()) return
    groups.push({
      groupId: groupId.trim(),
      teacherIds: pickStringArray(row, ['teacherIds', 'TeacherIds'])
    })
  })
  return groups
}

export type TestItemSopFields = {
  startDate: string | null
  endDate: string | null
  lmsVoteFormatType: number | null
  lmsVoteType: number | null
  lmsModuleId: string | null
  lmsModuleIds: string[]
  lmsVoteTeacherIds: string[]
  lmsVoteGroupIds: string[]
  lmsCustomVoteGroups: LmsCustomVoteGroup[]
}

export function pickTestItemSopFields(data: Record<string, unknown>): TestItemSopFields {
  return {
    startDate: pickDateString(data, ['startDate', 'StartDate', 'openAt', 'OpenAt']),
    endDate: pickDateString(data, ['endDate', 'EndDate', 'closeAt', 'CloseAt']),
    lmsVoteFormatType: pickNumber(data, ['lmsVoteFormatType', 'LMSVoteFormatType', 'LmsVoteFormatType']),
    lmsVoteType: pickNumber(data, ['lmsVoteType', 'LMSVoteType', 'LmsVoteType']),
    lmsModuleId: pickString(data, ['lmsModuleId', 'LMSModuleId', 'LmsModuleId']),
    lmsModuleIds: pickStringArray(data, ['lmsModuleIds', 'LMSModuleIds', 'LmsModuleIds']),
    lmsVoteTeacherIds: pickStringArray(data, [
      'lmsVoteTeacherIds',
      'LMSVoteTeacherIds',
      'LmsVoteTeacherIds',
    ]),
    lmsVoteGroupIds: pickStringArray(data, [
      'lmsVoteGroupIds',
      'LMSVoteGroupIds',
      'LmsVoteGroupIds',
    ]),
    lmsCustomVoteGroups: pickCustomVoteGroups(data, [
      'lmsCustomVoteGroups',
      'LMSCustomVoteGroups',
      'LmsCustomVoteGroups',
    ]),
  }
}

function withSopFields(payload: Record<string, unknown>): Record<string, unknown> {
  return { ...payload, ...pickTestItemSopFields(payload) }
}

export function unwrapTestItemPayload(raw: unknown): Record<string, unknown> | null {
  if (raw === null || typeof raw !== 'object') {
    return null
  }
  const root = raw as Record<string, unknown>
  const inner = root.data ?? root.Data
  if (inner !== null && typeof inner === 'object' && !Array.isArray(inner)) {
    const fromData = pickPayload(inner as Record<string, unknown>)
    if (fromData) {
      return withSopFields(fromData)
    }
    const d = inner as Record<string, unknown>
    return withSopFields({
      ...d,
      questions: Array.isArray(d.questions)
        ? d.questions
        : Array.isArray(d.Questions)
          ? d.Questions
          : []
    })
  }
  const fromRoot = pickPayload(root)
  return fromRoot ? withSopFields(fromRoot) : null
}

export const TEST_MANAGER_ITEM_STATUS_DRAFT = 0
export const TEST_MANAGER_ITEM_STATUS_PUBLISHED = 1
export const TEST_MANAGER_ITEM_STATUS_ARCHIVED = 2

function coalesceStatusNumber(v: unknown): number | null {
  if (typeof v === 'number' && Number.isFinite(v)) {
    return v
  }
  if (typeof v === 'string' && v.trim()) {
    const n = parseInt(v.trim(), 10)
    return Number.isFinite(n) ? n : null
  }
  return null
}

function isKnownManagerTestItemStatus(code: number): boolean {
  return (
    code === TEST_MANAGER_ITEM_STATUS_DRAFT ||
    code === TEST_MANAGER_ITEM_STATUS_PUBLISHED ||
    code === TEST_MANAGER_ITEM_STATUS_ARCHIVED
  )
}

export function pickManagerTestItemStatusCode(
  data: Record<string, unknown>
): number | null {
  const keys = ['status', 'Status', 'testItemStatus', 'TestItemStatus']
  for (const key of keys) {
    if (key in data) {
      const code = coalesceStatusNumber(data[key])
      if (code !== null && isKnownManagerTestItemStatus(code)) {
        return code
      }
    }
  }
  return null
}

export type FetchTestItemOptions = {
  accessToken?: string | null
  lmsModuleId?: string | null
}

export async function fetchTestItem(
  testItemId: string,
  options?: FetchTestItemOptions
): Promise<unknown> {
  const id = String(testItemId ?? '').trim()
  if (!id) {
    throw new Error('testItemId required')
  }

  let req = API.request()
    .endpoint(Endpoints.gateway)
    .url('testsservice/api/TestItem/Get')
    .addQuery('id', id)
    .get()

  const lmsModuleId = String(options?.lmsModuleId ?? '').trim()
  if (lmsModuleId) {
    req = req.addQuery('lmsModuleId', lmsModuleId)
  }

  const token = options?.accessToken?.trim()
  if (token) {
    req = req.headers({ Authorization: `Bearer ${token}` })
  }

  const response = await req.send()
  if (response.status === 204) {
    return null
  }
  const text = await response.text()

  if (!response.ok) {
    let msg = `HTTP ${response.status}`
    if (text.trim()) {
      try {
        const errBody = JSON.parse(text) as Record<string, unknown>
        const title = errBody.title
        const message = errBody.message ?? errBody.error
        if (typeof title === 'string') {
          msg = title
        } else if (typeof message === 'string') {
          msg = message
        }
      } catch {
      }
    }
    throw new Error(msg)
  }

  if (!text.trim()) {
    return {}
  }

  try {
    return JSON.parse(text) as unknown
  } catch {
    throw new Error('Invalid JSON response')
  }
}
